import { useMemo } from 'react';
import { InjuryReportData, PlayerInjury, GameInjuryReport, type ImpactLevel } from '../components/InjuryReportModal';
import { DisplayGame } from '../data/types';
import { nbaTeams } from '../data/nbaTeams';

// Normalize injury status to our standard format
function normalizeStatus(status?: string): PlayerInjury['status'] | null {
  if (!status) return null;
  const s = status.toLowerCase();
  if (s === 'out') return 'Out';
  if (s === 'doubtful') return 'Doubtful';
  if (s === 'questionable') return 'Questionable';
  if (s === 'probable') return 'Probable';
  return null;
}

// Calculate impact level based on injured players' projected minutes.
// Same rule for every league: leagues without projected minutes (MLB/NFL) get no badge.
// (A projection-based proxy was considered for the NFL and rejected: projections already
// price the injury in, so an Out player's workload would never clear a threshold.)
function calculateImpactLevel(injuries: PlayerInjury[]): ImpactLevel {
  // Include Out, Doubtful, and Questionable players for impact calculation
  const relevantInjuries = injuries.filter(
    i => i.status === 'Out' || i.status === 'Doubtful' || i.status === 'Questionable'
  );

  // High impact: any injured player with 25+ projected minutes
  if (relevantInjuries.some(i => (i.projectedMinutes || 0) >= 25)) {
    return 'high';
  }

  // Medium impact: any injured player with 15+ projected minutes
  if (relevantInjuries.some(i => (i.projectedMinutes || 0) >= 15)) {
    return 'medium';
  }

  return undefined;
}

// Transform games data into injury report format
function transformGamesToInjuryReport(games: DisplayGame[], league: string): InjuryReportData {
  const isNFL = league.toUpperCase() === 'NFL';
  const gameReports: GameInjuryReport[] = games.map((game) => {
    const injuries: PlayerInjury[] = [];

    // Process all players for injuries
    for (const player of game.players || []) {
      const status = normalizeStatus(player.injuryStatus);
      if (status) {
        // Parse projected minutes from player data
        const projectedMinutes = player.minutes ? parseFloat(player.minutes) : undefined;

        // Normalize player team to match one of the game's team abbreviations
        // Handles cases where API returns different abbreviation formats (e.g. "NO" vs "NOP")
        let team = player.team;
        if (team !== game.awayTeam.abbr && team !== game.homeTeam.abbr) {
          // Try to match by checking if one contains the other
          if (game.awayTeam.abbr.startsWith(team) || team.startsWith(game.awayTeam.abbr)) {
            team = game.awayTeam.abbr;
          } else if (game.homeTeam.abbr.startsWith(team) || team.startsWith(game.homeTeam.abbr)) {
            team = game.homeTeam.abbr;
          }
        }

        const position = player.position ? player.position.toUpperCase() : undefined;
        injuries.push({
          playerName: player.name,
          playerId: player.id,
          avatar: player.avatar,
          team,
          status,
          reason: player.injuryNotes || 'Injury/Illness',
          projectedMinutes: projectedMinutes && !isNaN(projectedMinutes) ? projectedMinutes : undefined,
          position,
          detail: isNFL ? position : undefined,
        });
      }
    }

    // Sort injuries: Out first, then Doubtful, Questionable, Probable
    const statusOrder = { Out: 0, Doubtful: 1, Questionable: 2, Probable: 3 };
    injuries.sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);

    // Check if game has high impact injuries (Out or Doubtful) - legacy field
    const hasHighImpact = injuries.some(
      (i) => i.status === 'Out' || i.status === 'Doubtful'
    );

    // Calculate impact level based on injured players' minutes (none for leagues without minutes)
    const impactLevel = calculateImpactLevel(injuries);

    return {
      gameId: game.id,
      startTime: game.startTime, // Store for sorting
      awayTeam: {
        name: game.awayTeam.name,
        abbr: game.awayTeam.abbr,
        logo: game.awayTeam.logo || nbaTeams[game.awayTeam.abbr]?.logo || '',
      },
      homeTeam: {
        name: game.homeTeam.name,
        abbr: game.homeTeam.abbr,
        logo: game.homeTeam.logo || nbaTeams[game.homeTeam.abbr]?.logo || '',
      },
      gameTime: game.time,
      hasHighImpact,
      impactLevel,
      injuries,
      // Since we're using our own data, teams are always "submitted"
      awayTeamSubmitted: true,
      homeTeamSubmitted: true,
    };
  });

  // Sort games by start time (chronological order)
  gameReports.sort((a, b) => {
    const timeA = a.startTime ? new Date(a.startTime).getTime() : 0;
    const timeB = b.startTime ? new Date(b.startTime).getTime() : 0;
    return timeA - timeB;
  });

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const lastUpdated = today.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return {
    date: dateStr,
    lastUpdated,
    games: gameReports,
  };
}

export function useInjuryReport(games: DisplayGame[], league: string = 'NBA') {
  const data = useMemo(() => {
    if (!games || games.length === 0) {
      return null;
    }
    return transformGamesToInjuryReport(games, league);
  }, [games, league]);

  return {
    data,
    isLoading: false,
    isFetching: false,
    refetch: () => {}, // No-op since data comes from props
  };
}

export default useInjuryReport;
